import React, { useEffect, useMemo, useState } from "react";
import CategoryTreeNavigator from "../category/CategoryTreeNavigator";

import { Category } from "../../types/Category";
import { Unit } from "../../types/Unit";
import InventoryComponent from "./InventoryComponent";
import InventorySearchPanel from "./InventorySearchPanel";

// IMPORTANT: use whichever file actually contains getCategoryById in your codebase
import { getCategoryById } from "../../api/CategoryAPI";
import { getAllUnits } from "../../api/UnitAPI";
import CategoryRequestPanel from "../categoryRequest/CategoryRequestPanel";
import { fetchStoreCategories } from "../../api/StoreAPI";
import { logApiError, getUserMessage } from "../../api/apiError";
import { parseStoreId } from "../../utils/jwt";


type Mode = "create" | "edit";

interface InventoryWorkspaceProps {
    token: string;
}




const InventoryWorkspace: React.FC<InventoryWorkspaceProps> = ({ token }) => {
    const [mode, setMode] = useState<Mode>("create");
    const [showCategoryRequest, setShowCategoryRequest] = useState(false);
    const [requestedCategoryName, setRequestedCategoryName] = useState("");
    const [requestSavedFor, setRequestSavedFor] = useState<string | null>(null);

    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [selectedInventoryId, setSelectedInventoryId] = useState<string | null>(null);
    const [deletedInventoryId, setDeletedInventoryId] = useState<string | null>(null);
    const [createNonce, setCreateNonce] = useState(0);
    const [editNonce, setEditNonce] = useState(0);

    const [units, setUnits] = useState<Unit[]>([]);

    // Store categories (edit mode dropdown)
    const [storeCategories, setStoreCategories] = useState<Category[]>([]);
    const [storeCategoryId, setStoreCategoryId] = useState<string>("");
    const [storeCatFilter, setStoreCatFilter] = useState<string>("");
    const [storeCatsLoading, setStoreCatsLoading] = useState(false);
    const [storeCatsError, setStoreCatsError] = useState<string | null>(null);

    // Edit mode: filter the store's categories by the typed search text.
    const filteredStoreCategories = useMemo(() => {
        const q = storeCatFilter.trim().toLowerCase();
        const matches = q
            ? storeCategories.filter((c) => c.name.toLowerCase().includes(q))
            : storeCategories;
        // Always list alphabetically so the box is scannable.
        return [...matches].sort((a, b) => a.name.localeCompare(b.name));
    }, [storeCategories, storeCatFilter]);

    useEffect(() => {
        getAllUnits(token).then(setUnits).catch(() => setUnits([]));
    }, [token]);

    // ✅ handler: called when user chooses a category
    const handleCategorySelect = (cat: Category | null) => {
        setSelectedCategory(cat);
        setSelectedInventoryId(null);
        setDeletedInventoryId(null);
        setCreateNonce((n) => n + 1);
    };

    // ✅ handler: user wants to create a new category (system-wide create flow)
    const handleCreateNewCategory = (initialName: string) => {
        console.log("Create new category from InventoryWorkspace:", initialName);
        // TODO: navigate to CategoryComponent route or open modal prefilled with initialName
    };

    const handleCreateNewInventory = () => {
        if (!selectedCategory) return;
        console.log("Creating new inventory in category:", selectedCategory.name);
        setSelectedInventoryId(null); // create mode
        setCreateNonce((n) => n + 1); // force remount editor
    };

    const handleInventoryPickedForEdit = (id: string) => {
        setSelectedInventoryId(id);
        setEditNonce((n) => n + 1);
    };

    // ✅ When mode becomes EDIT: load store categories and show dropdown
    useEffect(() => {
        if (mode !== "edit") return;

        let cancelled = false;

        // Accounts with no linked store (e.g. admin/root) have no listings to edit.
        if (!parseStoreId(token)) {
            setStoreCategories([]);
            setStoreCategoryId("");
            handleCategorySelect(null);
            setStoreCatsError(null);
            setStoreCatsLoading(false);
            return;
        }

        async function loadStoreCategories() {
            setStoreCatsLoading(true);
            setStoreCatsError(null);

            try {
                if (cancelled) return;
                const cats = await fetchStoreCategories(token);
                setStoreCategories(cats);

                // Reset selection when entering edit mode
                setStoreCategoryId("");
                setStoreCatFilter("");
                handleCategorySelect(null);
            } catch (err: any) {
                if (cancelled) return;

                logApiError(err);
                setStoreCategories([]);
                setStoreCategoryId("");
                handleCategorySelect(null);

                setStoreCatsError(getUserMessage(err));
            } finally {
                if (!cancelled) setStoreCatsLoading(false);
            }
        }

        loadStoreCategories();

        return () => {
            cancelled = true;
        };

    }, [mode, token]);

    return (
        <div className="inventory-workspace">
            <h2 style={{ marginBottom: "1.5rem" }}>Listing Management</h2>
            
            <div className="mode-toggle">
                <button
                    onClick={() => {
                        setMode("create");
                        setStoreCategoryId("");
                        setStoreCatsError(null);
                    }}
                    disabled={mode === "create"}
                >
                    Create Listing
                </button>

                <button
                    onClick={() => setMode("edit")}
                    disabled={mode === "edit"}
                >
                    Edit Listing
                </button>
            </div>

            {/* Category selection */}
            {mode === "create" ? (
                <>
                    <CategoryTreeNavigator
                        token={token}
                        selectedLeafId={selectedCategory?.id ?? null}
                        requestSavedFor={requestSavedFor}
                        createNewLabel="Request Category"
                        onSelectLeaf={(full) => {
                            handleCategorySelect(full);
                            // close request panel if open
                            setShowCategoryRequest(false);
                        }}
                        onCreateNew={(initialName) => {
                            if (requestSavedFor === initialName) {
                                return; // already saved, do nothing
                            }
                            setRequestedCategoryName(initialName);
                            setShowCategoryRequest(true);

                            // make sure no stale category is shown
                            handleCategorySelect(null);
                        }}
                    />

                    {/* ✅ SHOW REQUEST PANEL RIGHT AFTER SEARCH */}
                    {showCategoryRequest && (
                        <div style={{ marginTop: "1rem" }}>
                            <CategoryRequestPanel
                                token={token}
                                defaultName={requestedCategoryName}
                                onCreated={() => {
                                    setShowCategoryRequest(false);
                                    setRequestSavedFor(requestedCategoryName);
                                }}
                                onCancel={() => setShowCategoryRequest(false)}
                            />
                        </div>
                    )}
                </>
            ) : (
                <div className="section-box section-box--narrow">
                    <h4 style={{ marginBottom: "1rem" }}>Select Store Category</h4>

                    {storeCatsLoading && <div className="status-loading">Loading store categories…</div>}
                    {storeCatsError && (
                        <div className="status-error" style={{ marginBottom: "0.75rem" }}>{storeCatsError}</div>
                    )}

                    {!storeCatsLoading && storeCategories.length > 0 && (
                        <input
                            type="text"
                            value={storeCatFilter}
                            onChange={(e) => setStoreCatFilter(e.target.value)}
                            placeholder="Search categories…"
                            style={{ width: "100%", marginBottom: "0.5rem" }}
                        />
                    )}

                    {!storeCatsLoading && storeCategories.length > 0 && (
                        <>
                            {/* Always-open clickable list: typing above visibly narrows
                                it, and every click reliably selects (native <select>
                                listboxes swallow clicks in some browsers). */}
                            <div
                                style={{
                                    width: "100%",
                                    height: "16rem",
                                    overflowY: "auto",
                                    border: "1px solid var(--border-color)",
                                    borderRadius: "var(--radius-sm, 4px)",
                                    background: "var(--bg-primary)",
                                }}
                            >
                                {filteredStoreCategories.map((c) => (
                                    <div
                                        key={c.id}
                                        onClick={async () => {
                                            setStoreCategoryId(c.id);
                                            try {
                                                const full = await getCategoryById(token, c.id);
                                                handleCategorySelect(full);
                                            } catch (err) {
                                                logApiError(err);
                                                setStoreCatsError(getUserMessage(err));
                                                handleCategorySelect(null);
                                            }
                                        }}
                                        style={{
                                            padding: "0.35rem 0.5rem",
                                            cursor: "pointer",
                                            userSelect: "none",
                                            background: c.id === storeCategoryId
                                                ? "var(--accent-soft, #e6f4ea)"
                                                : undefined,
                                            fontWeight: c.id === storeCategoryId ? 600 : 400,
                                            boxShadow: c.id === storeCategoryId
                                                ? "inset 3px 0 0 var(--accent, #2e7d32)"
                                                : undefined,
                                        }}
                                    >
                                        {c.name}
                                    </div>
                                ))}
                            </div>
                            <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
                                {filteredStoreCategories.length} of {storeCategories.length} categories
                                {storeCatFilter.trim() ? ` matching “${storeCatFilter.trim()}”` : ""}
                            </div>
                        </>
                    )}

                    {!storeCatsLoading && storeCategories.length > 0 &&
                        filteredStoreCategories.length === 0 && (
                            <div style={{ color: "var(--text-muted)", marginTop: "0.5rem" }}>
                                No categories match “{storeCatFilter}”.
                            </div>
                        )}

                    {!storeCatsLoading && storeCategories.length === 0 && !storeCatsError && (
                        <div style={{ color: "var(--text-muted)" }}>
                            {parseStoreId(token)
                                ? "No categories found for this store."
                                : "Your account isn't linked to a store, so there are no listings to edit. Sign in as a store user to edit listings."}
                        </div>
                    )}
                </div>
            )}

            {selectedCategory && (
                <div style={{ marginTop: "1.5rem" }}>
                    <div className="status-selected" style={{ marginBottom: "1rem" }}>
                        Selected Category: {selectedCategory.name}
                    </div>
                    {/* CREATE MODE */}
                    {mode === "create" && selectedCategory && (
                        <div style={{ marginTop: "1rem" }}>
                            <InventoryComponent
                                key={`create-${selectedCategory.id}-${createNonce}`}
                                token={token}
                                initialCategoryId={selectedCategory.id}
                                categoryHasServiceArea={selectedCategory.hasServiceArea}
                                categoryHasItemLocation={selectedCategory.hasItemLocation}
                                initialCategoryName={selectedCategory.name}
                                categoryParameters={selectedCategory.parameters}
                                units={units}
                            />
                        </div>
                    )}

                    {/* EDIT MODE */}
                    {mode === "edit" && selectedCategory && (
                        <div style={{ marginTop: "1rem" }}>
                            {/* Search FIRST */}
                            <InventorySearchPanel
                                token={token}
                                category={selectedCategory}
                                units={units}
                                onInventorySelect={handleInventoryPickedForEdit}
                                deletedInventoryId={deletedInventoryId}
                            />

                            {/* Editor ONLY after selection */}
                            {selectedInventoryId && (
                                <div style={{ marginTop: "1rem" }}>
                                    <InventoryComponent
                                        key={`edit-${selectedInventoryId}-${editNonce}`}
                                        token={token}
                                        initialCategoryId={selectedCategory.id}
                                        categoryHasServiceArea={selectedCategory.hasServiceArea}
                                        categoryHasItemLocation={selectedCategory.hasItemLocation}
                                        initialCategoryName={selectedCategory.name}
                                        initialInventoryId={selectedInventoryId}
                                        categoryParameters={selectedCategory.parameters}
                                        units={units}
                                        onDeleted={(id) => setDeletedInventoryId(id)}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                </div>
            )}
        </div>
    );
};

export default InventoryWorkspace;